import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import { mergeDeepRight } from 'ramda'

import { KEYS, localStorageService } from 'utils/localStorage'
import { redirectToLoginIfUnauthorized } from 'utils/sessionUnauthorized'

export interface IHttpClient {
  storage?: typeof localStorageService
  apiUrl?: string
}

export type HttpClientRequestConfig = InternalAxiosRequestConfig & {
  /** Do not attach Bearer token (public preview / package / iframe API). */
  skipAuth?: boolean
}

export class HttpClient {
  storage: typeof localStorageService
  apiUrl: string
  api: AxiosInstance

  constructor({
    storage = localStorageService,
    apiUrl = import.meta.env.REACT_APP_API_URL
  }: IHttpClient) {
    this.storage = storage
    // @ts-ignore
    this.apiUrl = apiUrl

    this.api = axios.create({
      baseURL: apiUrl
    })

    this.api.interceptors.response.use(
      response => response,
      error => {
        redirectToLoginIfUnauthorized(error.response?.status)

        return Promise.reject(error)
      }
    )
  }

  mergeConfig(config: Record<string, unknown> = {}) {
    const skipAuth = config.skipAuth === true
    const { skipAuth: _omit, ...rest } = config
    const authToken = skipAuth ? null : this.storage.get(KEYS.token)
    const authHeader = authToken ? { Authorization: `Bearer ${authToken}` } : {}

    const defaultConfig = {
      headers: {
        ...authHeader
      }
    }

    const axiosConfig = mergeDeepRight(defaultConfig)(rest) as Record<
      string,
      unknown
    >

    delete axiosConfig.skipAuth

    return axiosConfig
  }

  get<T = unknown>(
    url: string,
    config = {}
  ): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, this.mergeConfig(config))
  }

  post<T = unknown>(
    url: string,
    data = {},
    config = {}
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, this.mergeConfig(config))
  }

  /** Multipart uploads — lets axios set `Content-Type` + boundary for `FormData`. */
  postFormData(url: string, formData: FormData, config = {}) {
    // @ts-ignore
    return this.api.post(url, formData, this.mergeConfig(config))
  }

  /** PATCH with multipart body — same as postFormData; do not set Content-Type manually. */
  patchFormData(url: string, formData: FormData, config = {}) {
    // @ts-ignore
    return this.api.patch(url, formData, this.mergeConfig(config))
  }

  put<T = unknown>(
    url: string,
    data = {},
    config = {}
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, this.mergeConfig(config))
  }

  patch<T = unknown>(
    url: string,
    data = {},
    config = {}
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, this.mergeConfig(config))
  }

  /** Axios DELETE config (headers, params, `data` for body). Same shape as `get(url, config)`. */
  delete<T = unknown>(
    url: string,
    config: Record<string, unknown> = {}
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, this.mergeConfig(config))
  }
}

 
export default new HttpClient({})
